import { getApiEndpoints } from '@/services/api/endpoints';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type {
  ConfirmBusinessUserRequest,
  ConfirmBusinessUserResponse,
  ListBusinessUsersEnvelope,
  ListBusinessUsersResponse,
  UpdateBusinessUserRequest,
  UpdateBusinessUserEnabledRequest,
  UpdateBusinessUserEnabledResponse,
  UpdateBusinessUserResponse,
} from './businessUsersPoolTypes';

export type FetchBusinessUsersPoolArgs = {
  limit?: number;
  nextToken?: string;
};

const DEFAULT_LIMIT = 25;
const MIN_LIMIT = 1;
const MAX_LIMIT = 60;

function normalizeLimit(limit?: number) {
  if (typeof limit !== 'number' || Number.isNaN(limit)) {
    return DEFAULT_LIMIT;
  }

  return Math.min(MAX_LIMIT, Math.max(MIN_LIMIT, Math.trunc(limit)));
}

export const fetchBusinessUsersPool = createAppAsyncThunk<ListBusinessUsersResponse, FetchBusinessUsersPoolArgs | void>(
  'businessUsersPool/fetchBusinessUsersPool',
  async (arg, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const limit = normalizeLimit(arg?.limit);
      const nextToken = arg?.nextToken?.trim();

      const params = new URLSearchParams({ limit: String(limit) });
      if (nextToken) {
        params.set('nextToken', nextToken);
      }

      const endpoint = `${getApiEndpoints().authAdmin.listBusinessUsers}?${params.toString()}`;
      logger?.info('[fetchBusinessUsersPool] Fetching business Cognito users', {
        limit,
        hasNextToken: Boolean(nextToken),
      });

      const response = await apiService.get<ListBusinessUsersEnvelope>(endpoint, { suppressLogging: true });
      if (!response.data || !Array.isArray(response.data.users)) {
        throw new Error('Business users pool returned an invalid response.');
      }
      const result: ListBusinessUsersResponse = {
        success: true,
        users: response.data.users,
        nextToken: response.data.nextToken,
      };
      logger?.debug('[fetchBusinessUsersPool] Response received', {
        users: result.users.length,
        hasNextToken: result.nextToken !== null,
      });

      return result;
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[fetchBusinessUsersPool] Failed to fetch business Cognito users', {
        message: error.message,
        status: error.status,
      });
      return rejectWithValue({ message: error.message || 'Failed to fetch business users pool' });
    }
  },
  {
    condition: (_, { getState }) => {
      return !getState().businessUsersPool.isLoading;
    },
  },
);

export const updateBusinessUser = createAppAsyncThunk<UpdateBusinessUserResponse, UpdateBusinessUserRequest>(
  'businessUsersPool/updateBusinessUser',
  async (payload, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().authAdmin.updateBusinessUser;
      logger?.info('[updateBusinessUser] Updating business Cognito user attributes', {
        endpoint,
        username: payload.username,
        attributes: Object.keys(payload.attributes),
      });

      const response = await apiService.put<UpdateBusinessUserResponse, UpdateBusinessUserRequest>(endpoint, payload);
      logger?.debug('[updateBusinessUser] Response received', {
        username: response.user.username,
      });

      return response;
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[updateBusinessUser] Failed to update business Cognito user', {
        username: payload.username,
        message: error.message,
        status: error.status,
        body: error.body,
      });
      return rejectWithValue({ message: error.message || 'Failed to update business user' });
    }
  },
  {
    condition: (_, { getState }) => {
      return !getState().businessUsersPool.isUpdating;
    },
  },
);

export const updateBusinessUserEnabled = createAppAsyncThunk<
  UpdateBusinessUserEnabledResponse,
  UpdateBusinessUserEnabledRequest
>(
  'businessUsersPool/updateBusinessUserEnabled',
  async (payload, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().authAdmin.updateBusinessUserEnabled;
      logger?.info('[updateBusinessUserEnabled] Updating business Cognito user enabled state', {
        endpoint,
        username: payload.username,
        enabled: payload.enabled,
      });

      const response = await apiService.put<UpdateBusinessUserEnabledResponse, UpdateBusinessUserEnabledRequest>(
        endpoint,
        payload,
      );
      logger?.debug('[updateBusinessUserEnabled] Response received', {
        username: response.user.username,
        enabled: response.user.enabled,
      });

      return response;
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[updateBusinessUserEnabled] Failed to update enabled state', {
        username: payload.username,
        enabled: payload.enabled,
        message: error.message,
        status: error.status,
        body: error.body,
      });
      return rejectWithValue({ message: error.message || 'Failed to update business user enabled state' });
    }
  },
  {
    condition: (_, { getState }) => {
      return !getState().businessUsersPool.isUpdating;
    },
  },
);

export const confirmBusinessUser = createAppAsyncThunk<ConfirmBusinessUserResponse, ConfirmBusinessUserRequest>(
  'businessUsersPool/confirmBusinessUser',
  async (payload, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().authAdmin.confirmBusinessUser;
      logger?.info('[confirmBusinessUser] Confirming business Cognito user', {
        endpoint,
        username: payload.username,
      });

      const response = await apiService.post<ConfirmBusinessUserResponse, ConfirmBusinessUserRequest>(
        endpoint,
        payload,
      );
      logger?.debug('[confirmBusinessUser] Response received', {
        username: response.user.username,
        userStatus: response.user.userStatus,
      });

      return response;
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[confirmBusinessUser] Failed to confirm business user', {
        username: payload.username,
        message: error.message,
        status: error.status,
        body: error.body,
      });
      return rejectWithValue({ message: error.message || 'Failed to confirm business user' });
    }
  },
  {
    condition: (_, { getState }) => {
      return !getState().businessUsersPool.isUpdating;
    },
  },
);
